/**
 * Subscription page utilities.
 * Builds hub profiles and resolves subscription data for a selected hub from decodedToken.
 */

import { SUBSCRIPTION_CONFIG } from "../../config/zinihubConfig";
import { getPlanSnapshot } from "../../utils/subscriptionState";

/** Validity values sent in `subscription.validity` (lowercase, API-friendly). */
export const VALIDITY = {
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  ANNUAL: "annual",
};

/**
 * Validity options for Renew / Upgrade. `discountPercent` is separate from the "off" wording
 * so labels can be driven by backend or i18n later.
 */
export const VALIDITY_OPTIONS = [
  { value: VALIDITY.MONTHLY, label: "Monthly", discountPercent: null },
  { value: VALIDITY.QUARTERLY, label: "Quarterly", discountPercent: 2 },
  { value: VALIDITY.ANNUAL, label: "Annual", discountPercent: 5 },
];

/** Suffix after the percent (e.g. "5% off") — swap for i18n or API later. */
export const DISCOUNT_OFF_SUFFIX = "off";

/**
 * @param {string} validityValue
 * @returns {string}
 */
export const getValidityLabel = (validityValue) =>
  VALIDITY_OPTIONS.find((o) => o.value === validityValue)?.label ?? validityValue;

/**
 * Get plans available for upgrade from current plan.
 * Returns higher catalog plans using the supplied plan ordering.
 * @param {string} currentPlan - Current plan (lowercase)
 * @returns {string[]}
 */
export const getUpgradeOptions = (currentPlan, paidPlans = []) => {
  const plan = (currentPlan || "").toLowerCase();
  const currentIndex = paidPlans.indexOf(plan);
  return currentIndex < 0 ? [] : paidPlans.slice(currentIndex + 1);
};

/**
 * Get plans available for downgrade from current plan.
 * Returns lower catalog plans using the supplied plan ordering.
 * @param {string} currentPlan - Current plan (lowercase)
 * @returns {string[]}
 */
export const getDowngradeOptions = (currentPlan, paidPlans = []) => {
  const plan = (currentPlan || "").toLowerCase();
  const currentIndex = paidPlans.indexOf(plan);
  return currentIndex <= 0 ? [] : paidPlans.slice(0, currentIndex).reverse();
};

/**
 * Check if the current plan has no higher catalog plan.
 * @param {string} currentPlan
 * @returns {boolean}
 */
export const isUpgradeDisabled = (currentPlan, paidPlans = []) => {
  return getUpgradeOptions(currentPlan, paidPlans).length === 0;
};

/**
 * Check if the current plan has no lower catalog plan.
 * @param {string} currentPlan
 * @returns {boolean}
 */
export const isDowngradeDisabled = (currentPlan, paidPlans = []) => {
  const plan = (currentPlan || "").toLowerCase();
  return getDowngradeOptions(plan, paidPlans).length === 0;
};

/**
 * Get display name for a hub profile (same logic as Stores.js).
 * @param {object} profile - HubProfile from token
 * @returns {string}
 */
export const getHubDisplayName = (profile) => {
  if (!profile) return "";
  return profile.DisplayName || profile.Domain || "";
};

/**
 * Build list of hub profiles from decodedToken (admin hubs + staff assignments).
 * Supports multiple hubs: iterates all MyHubAdmin entries that have HubProfile.
 * @param {object} decodedToken - Decoded JWT payload
 * @returns {Array<object>} Array of unique hub profiles (by PK)
 */
export const getHubProfiles = (decodedToken) => {
  const profiles = [];

  const addProfile = (profile) => {
    if (!profile?.PK) return;
    profiles.push(profile);
  };

  const myHubAdmin = decodedToken?.MyHubAdmin;
  if (Array.isArray(myHubAdmin)) {
    myHubAdmin.forEach((entry) => {
      const hubProfile = entry?.HubProfile;
      if (!hubProfile) return;
      if (Array.isArray(hubProfile)) {
        hubProfile.forEach(addProfile);
      } else {
        addProfile(hubProfile);
      }
    });
  }

  const staffAssignments = decodedToken?.StaffAssignments;
  if (Array.isArray(staffAssignments)) {
    staffAssignments.forEach((assignment) => {
      addProfile(assignment?.HubProfile);
    });
  }

  const uniqueByPk = new Map();
  profiles.forEach((p) => {
    if (p?.PK && !uniqueByPk.has(p.PK)) uniqueByPk.set(p.PK, p);
  });
  return Array.from(uniqueByPk.values());
};

/**
 * Get subscription record for a specific hub (Plan, ExpiryDate, StartDate, etc.).
 * Finds the MyHubAdmin entry whose HubProfile.PK matches hubId.
 * @param {object} decodedToken - Decoded JWT payload
 * @param {string} hubId - Hub PK (e.g. "HUB#eff4affe-6158-46be-9815-97e8a771f232")
 * @returns {object|null} Subscription fields or null if not found
 */
export const getSubscriptionForHub = (decodedToken, hubId) => {
  if (!decodedToken || !hubId) return null;
  const ownerEntry = (decodedToken.MyHubAdmin || []).find(
    (item) => item?.HubProfile?.PK === hubId
  );
  const staffEntry = (decodedToken.StaffAssignments || []).find(
    (item) => item?.HubProfile?.PK === hubId
  );
  const entry = ownerEntry || staffEntry;
  if (!entry) return null;
  const planSnapshot = getPlanSnapshot(entry.HubProfile);
  return {
    Plan: planSnapshot.plan,
    ExpiryDate: planSnapshot.expiry,
    StartDate: entry.HubProfile?.StartDate ?? entry.StartDate ?? null,
    BillingCycle: planSnapshot.billingCycle,
    CatalogVersion: planSnapshot.catalogVersion,
    CatalogChecksum: planSnapshot.catalogChecksum,
    SubscriptionStatus: entry.HubProfile?.SubscriptionStatus ?? entry.SubscriptionStatus,
    LastAccessedOn: entry.LastAccessedOn,
    LastAccessedBy: entry.LastAccessedBy,
    IsOwner: Boolean(ownerEntry),
    HubProfile: entry.HubProfile,
  };
};

export { formatSubscriptionDateTime as formatSubscriptionDate } from "../../utils/subscriptionDisplay";

/**
 * Format date and time for "last accessed" display.
 * @param {string} dateStr - ISO date string
 * @returns {string}
 */
export const formatLastAccessDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return dateStr;
  }
};


const MS_PER_DAY = 1000 * 60 * 60 * 24;

export const getSubscriptionStatus = (startDate, expiryDate) => {
  if (!startDate || !expiryDate) {
    return {
      totalDays: 0,
      remainingDays: 0,
      progressRatio: 0,
      urgency: "none",
    };
  }

  const start = new Date(startDate);
  const expiry = new Date(expiryDate);
  const today = new Date();

  const totalDays = Math.ceil((expiry - start) / MS_PER_DAY);
  const remainingRaw = Math.ceil((expiry - today) / MS_PER_DAY);
  const remainingDays = remainingRaw > 0 ? remainingRaw : 0;

  const progressRatio =
    totalDays > 0 ? 1 - remainingDays / totalDays : 0;

  let urgency = "normal";

  if (remainingDays <= 0) urgency = "expired";
  else if (remainingDays <= 1) urgency = "critical";
  else if (remainingDays <= 3) urgency = "high";
  else if (remainingDays <= 7) urgency = "medium";

  return {
    totalDays,
    remainingDays,
    progressRatio: Math.min(Math.max(progressRatio, 0), 1),
    urgency,
  };
};

export const getProgressBarState = (startDate, expiryDate) => {
  const { progressRatio, urgency, remainingDays } =
    getSubscriptionStatus(startDate, expiryDate);

  let strokeColor = "#52c41a"; // green

  if (urgency === "medium") strokeColor = "#faad14";
  else if (urgency === "high") strokeColor = "#fa8c16";
  else if (urgency === "critical") strokeColor = "#ff4d4f";
  else if (urgency === "expired") strokeColor = "#8b0000";

  return {
    percent: Math.round(progressRatio * 100),
    strokeColor,
    remainingDays,
  };
};

/**
 * Trial always sees upgrade options; paid plans only inside the final-days window.
 * @param {boolean} isTrial
 * @param {number} remainingDays
 * @returns {boolean}
 */
export const shouldShowUpgradeOptions = (isTrial, remainingDays) => {
  if (isTrial) return true;
  return remainingDays <= SUBSCRIPTION_CONFIG.UPGRADE_OPTIONS_FINAL_DAYS;
};

/**
 * Expiry card line: "Expired" or "N day(s) remaining".
 * @param {number} remainingDays
 * @returns {string}
 */
export const formatSubscriptionRemainingDaysLabel = (remainingDays) => {
  const { EXPIRY } = SUBSCRIPTION_CONFIG.LABELS;
  if (remainingDays <= 0) return EXPIRY.expired;
  return `${remainingDays} day${remainingDays > 1 ? "s" : ""} remaining`;
};

/**
 * Upgrade modal info alert when paid user is outside the upgrade window.
 * @returns {string}
 */
export const getUpgradeWindowBlockedDescription = () => {
  const template = SUBSCRIPTION_CONFIG.LABELS.UPGRADE_MODAL.alertDescription;
  return template.replace(
    "{finalDays}",
    String(SUBSCRIPTION_CONFIG.UPGRADE_OPTIONS_FINAL_DAYS)
  );
};
